// Servicio de envío de emails
// Intenta conectar con la API real, si no está disponible usa fallback

import { sendEmailAPI, getEmailTemplates as getEmailTemplatesAPI, updateEmailTemplates as updateEmailTemplatesAPI } from './apiService';

/**
 * Obtiene las plantillas de email personalizadas desde la API o localStorage como fallback
 */
export const getEmailTemplates = async () => {
  try {
    const response = await getEmailTemplatesAPI();
    if (response.success && response.templates) {
      // Guardar en localStorage como caché
      localStorage.setItem('emailTemplates', JSON.stringify(response.templates));
      return response.templates;
    }
  } catch (error) {
    console.warn('⚠️ Error obteniendo plantillas desde API, usando localStorage:', error);
  }
  
  // Fallback a localStorage
  const templates = localStorage.getItem('emailTemplates');
  if (templates) {
    return JSON.parse(templates);
  }
  
  // Plantillas por defecto (solo si no hay nada en localStorage)
  return {
    solicitudInicial: {
      asunto: 'Solicitud de datos trimestrales - {startupNombre} - {reporteNombre}',
      cuerpo: `Hola {fundadorNombre},

Espero que te encuentres bien. Te escribo para solicitar la información trimestral de {startupNombre} correspondiente al {reporteNombre}.

Necesitamos los siguientes datos para completar el reporte trimestral del club:

**KPIs Financieros:**
- Ingresos del trimestre
- Gastos del trimestre
- Número de empleados actual
- Runway (meses de capital disponible)
- ¿Tienes una ronda de financiación abierta? (Sí/No)
- Si es así: valoración y progreso de la ronda

**Hitos Relevantes del Trimestre:**
Por favor, comparte los hitos más importantes del trimestre en las siguientes áreas:
- Comercial
- Producto
- Tecnología
- Recursos Humanos
- Financiero
- General
- Otros

Puedes enviar esta información en el formato que prefieras. Si tienes alguna pregunta, no dudes en contactarme.

Gracias por tu colaboración.

Saludos cordiales`
    },
    recordatorio: {
      asunto: 'Recordatorio - Datos trimestrales pendientes - {startupNombre} - {reporteNombre}',
      cuerpo: `Hola {fundadorNombre},

Te escribo para recordarte que aún necesitamos la información trimestral de {startupNombre} para el {reporteNombre}.

Por favor, cuando tengas un momento, comparte los siguientes datos:

**KPIs Financieros:**
- Ingresos del trimestre
- Gastos del trimestre
- Número de empleados actual
- Runway (meses de capital disponible)
- ¿Tienes una ronda de financiación abierta? (Sí/No)
- Si es así: valoración y progreso de la ronda

**Hitos Relevantes del Trimestre:**
Los hitos más importantes del trimestre en las áreas: Comercial, Producto, Tecnología, RRHH, Financiero, General, Otros.

Si ya enviaste esta información y no la hemos recibido, por favor reenvíala.

Gracias por tu atención.

Saludos cordiales`
    },
    recordatorioFinal: {
      asunto: 'Recordatorio Final - Datos trimestrales - {startupNombre} - {reporteNombre}',
      cuerpo: `Hola {fundadorNombre},

Este es un recordatorio final para solicitar la información trimestral de {startupNombre} correspondiente al {reporteNombre}.

Es importante que recibamos esta información lo antes posible para poder completar el reporte trimestral del club.

**KPIs Financieros:**
- Ingresos del trimestre
- Gastos del trimestre
- Número de empleados actual
- Runway (meses de capital disponible)
- ¿Tienes una ronda de financiación abierta? (Sí/No)
- Si es así: valoración y progreso de la ronda

**Hitos Relevantes del Trimestre:**
Los hitos más importantes del trimestre en las áreas: Comercial, Producto, Tecnología, RRHH, Financiero, General, Otros.

Por favor, envíanos esta información a la mayor brevedad posible.

Gracias por tu colaboración.

Saludos cordiales`
    }
  };
};

/**
 * Guarda las plantillas de email personalizadas en la API y localStorage
 */
export const saveEmailTemplates = async (templates) => {
  try {
    console.log('📝 Guardando plantillas:', {
      tipos: Object.keys(templates),
      templates: templates
    });
    
    const response = await updateEmailTemplatesAPI(templates);
    
    console.log('📥 Respuesta del servidor:', response);
    
    if (response.success) {
      // Guardar también en localStorage como caché
      localStorage.setItem('emailTemplates', JSON.stringify(response.templates || templates));
      console.log('✅ Plantillas guardadas correctamente');
      return response.templates || templates;
    } else {
      throw new Error(response.error || 'Error al guardar plantillas');
    }
  } catch (error) {
    console.error('❌ Error guardando plantillas en API:', error);
    console.error('❌ Stack:', error.stack);
    // Fallback a localStorage
    localStorage.setItem('emailTemplates', JSON.stringify(templates));
    throw error;
  }
};

/**
 * Reemplaza las variables en la plantilla
 */
export const processTemplate = (template, variables) => {
  let processed = template;
  Object.keys(variables).forEach(key => {
    const regex = new RegExp(`{${key}}`, 'g');
    processed = processed.replace(regex, variables[key] || '');
  });
  return processed;
};

/**
 * Envía un email
 */
export const sendEmail = async (to, subject, body, tipo = 'solicitud') => {
  try {
    // Intentar enviar email real a través de la API
    const result = await sendEmailAPI({ 
      to, 
      subject, 
      body,
      tipo 
    });
    
    // Guardar en historial
    const historial = JSON.parse(localStorage.getItem('emailHistory') || '[]');
    const emailRecord = {
      id: result.id || Date.now(),
      to,
      subject,
      body,
      tipo,
      fecha: new Date().toISOString(),
      estado: 'enviado',
      apiResponse: result
    };
    
    historial.push(emailRecord);
    localStorage.setItem('emailHistory', JSON.stringify(historial));
    
    console.log('✅ Email enviado exitosamente:', { to, subject, tipo });
    return emailRecord;
    
  } catch (error) {
    // Si hay error de red o la API no está disponible, usar fallback
    console.error('❌ Error enviando email:', error);
    console.warn('⚠️ API no disponible, usando modo fallback:', error.message);
    
    // Si el error tiene un mensaje de warning del backend, mostrarlo
    if (error.message && error.message.includes('guardado pero no enviado')) {
      // El backend respondió pero no pudo enviar el email
      return await sendEmailFallback(to, subject, body, tipo);
    }
    
    return await sendEmailFallback(to, subject, body, tipo);
  }
};

/**
 * Fallback: Guarda el email en localStorage cuando la API no está disponible
 */
const sendEmailFallback = async (to, subject, body, tipo) => {
  // Simular delay de red
  await new Promise(resolve => setTimeout(resolve, 500));
  
  // Guardar historial de emails enviados
  const historial = JSON.parse(localStorage.getItem('emailHistory') || '[]');
  const emailRecord = {
    id: Date.now(),
    to,
    subject,
    body,
    tipo,
    fecha: new Date().toISOString(),
    estado: 'pendiente', // Marcar como pendiente porque no se envió realmente
    modo: 'fallback' // Indicar que se guardó en modo fallback
  };
  
  historial.push(emailRecord);
  localStorage.setItem('emailHistory', JSON.stringify(historial));
  
  console.log('Email guardado en modo fallback (API no disponible):', { to, subject, tipo });
  console.warn('⚠️ El email NO se ha enviado realmente. Configura la API para enviar emails.');
  
  return emailRecord;
};

/**
 * Obtiene el historial de emails enviados
 */
export const getEmailHistory = (startupId = null) => {
  const historial = JSON.parse(localStorage.getItem('emailHistory') || '[]');
  if (startupId) {
    return historial.filter(email => email.to === startupId);
  }
  return historial;
};

/**
 * Obtiene el último email enviado a una startup
 */
export const getLastEmailToStartup = (startupEmail) => {
  const historial = JSON.parse(localStorage.getItem('emailHistory') || '[]');
  const emailsToStartup = historial
    .filter(email => email.to === startupEmail)
    .sort((a, b) => new Date(b.fecha) - new Date(a.fecha));
  
  return emailsToStartup.length > 0 ? emailsToStartup[0] : null;
};

